import React, { forwardRef, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Highlighter, Typography } from '@onsolve/onsolve-ui-components';

import configService from '@services/config.service';
import jwtService from '@services/jwt.service';
import keyCodes from '@components/GlobalSearch/Utils/keyCodes';
import RedirectForm from '@components/RedirectForm/RedirectForm';

const SearchResultItem = forwardRef(
    ({ className, breadCrumb, term, title, tabIndex, onSearchSelection, item }, ref) => {
        const formRef = useRef(null);
        const { redirectTo, redirectBaseUrl, onClick } = item;

        const url = redirectBaseUrl && configService[redirectBaseUrl];

        const handleClick = useCallback(
            e => {
                e.preventDefault();
                onClick ? onClick(e) : onSearchSelection(item);
                // Do not initiate redirection if the token has expired - otherwise a user will
                // be redirected to an incorrect page.
                if (formRef.current && !jwtService.isTokenExpired()) {
                    formRef.current.submit();
                }
            },
            [item, onSearchSelection, onClick]
        );

        const handleKeyDown = useCallback(
            e => {
                const { keyCode } = e;
                const { ENTER_KEY } = keyCodes;

                if (keyCode === ENTER_KEY) {
                    onClick ? onClick(e) : onSearchSelection(item);
                    if (formRef.current) {
                        formRef.current.submit();
                    }
                }
            },
            [item, onSearchSelection, onClick]
        );
        return (
            <div
                data-test="search-result-item"
                className={className}
                tabIndex={tabIndex}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                ref={ref}
            >
                <Typography variant="p16" className="mb-1">
                    <Highlighter search={term} className="text-primary font-weight-bold">
                        {title}
                    </Highlighter>
                </Typography>
                <Typography variant="p12" className="mb-0" color="secondary">
                    {breadCrumb}
                </Typography>
                {redirectTo && url && <RedirectForm ref={formRef} to={`${url}${redirectTo}`} />}
            </div>
        );
    }
);
SearchResultItem.displayName = "SearchResultItem"

SearchResultItem.propTypes = {
    breadCrumb: PropTypes.string,
    className: PropTypes.string,
    item: PropTypes.object,
    onSearchSelection: PropTypes.func,
    tabIndex: PropTypes.string,
    term: PropTypes.string,
    title: PropTypes.string
};

export default SearchResultItem;
